import {
  SEPTEMBER_EXTRA_PROGRAM_KEY,
  SEPTEMBER_SCRAMBLE_PROGRAM_KEY,
} from '../constants/septemberScramble';

/**
 * Whether `key` resolves to a program in the `referral-program-configuration` flag — the
 * key → program-name mapping an operator edits in LaunchDarkly to point a campaign at a
 * program without a deploy.
 *
 * Mirrors async_get_referral_program_configuration() in diffit-api's feature_flags.py,
 * including its all-or-nothing rule: a mapping with any malformed entry is rejected whole
 * rather than filtered down to the good entries. A client that kept the good ones would
 * advertise a campaign the server resolves nothing for.
 *
 * A non-object variation is "not configured" — that covers the `false` LD serves for an
 * unset flag and the `undefined` it serves before the client has loaded, so a caller that
 * renders on this fails closed while flags are in flight.
 */
export const isReferralProgramKeyConfigured = (config, key) => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return false;
  }
  const entries = Object.entries(config);
  const isWellFormed = entries.every(
    ([mappedKey, name]) => mappedKey.trim() && typeof name === 'string' && name.trim()
  );
  if (!isWellFormed) {
    return false;
  }
  // Compared against the trimmed key because the backend trims the mapping before looking
  // anything up in it, so a stray space in the flag resolves there and must resolve here.
  return entries.some(([mappedKey]) => mappedKey.trim() === key);
};

/**
 * Whether this user may reach /referral — admins always, teachers once an operator points
 * bts_2026_september_scramble at a program. Shared by ReferralRouteGate and the profile
 * menu's "Share Diffit" item so the link can't offer a page the gate turns away.
 */
export const canAccessReferralPage = (diffitUser, referralProgramConfiguration) =>
  !!diffitUser?.is_diffit_admin ||
  isReferralProgramKeyConfigured(referralProgramConfiguration, SEPTEMBER_SCRAMBLE_PROGRAM_KEY);
